// CustomerReviews.jsx
import React from 'react';
import {
  Box,
  CardContent,
  Typography,
  Stack,
  Rating,
  Divider,
  Chip,
} from '@mui/material';

import {
  ReviewsSection,
  ReviewsCard,
  ReviewsHeader,
  ReviewsHeaderRow,
  ReviewsTitle,
  ReviewsSummary,
  SummaryLeft,
  SummaryRight,
  ReviewRow,
  AvatarBrand,
  NameAndMeta,
  RatingLine,
} from './CustomerReviews.styled';

export default function CustomerReviews({ reviews = [], title = 'Customer Reviews' }) {
  const published = Array.isArray(reviews) ? reviews.filter((r) => r?.IsPublished) : [];

  const sorted = [...published].sort((a, b) => {
    const da = new Date(a?.CreatedDate || 0).getTime();
    const db = new Date(b?.CreatedDate || 0).getTime();
    return db - da;
  });

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getInitials = (first, last) => {
    const f = (first || '').trim();
    const l = (last || '').trim();
    const a = f ? f[0] : '';
    const b = l ? l[0] : '';
    return (a + b).toUpperCase() || '?';
  };

  const fullName = (first, last) =>
    `${(first || '').trim()} ${(last || '').trim()}`.trim() || 'Anonymous';

  const avg =
    sorted.length > 0
      ? Math.round(
          (sorted.reduce((sum, r) => sum + (Number(r?.Rating) || 0), 0) / sorted.length) * 10
        ) / 10
      : 0;

  return (
    <ReviewsSection>
      <ReviewsCard elevation={0}>
        <ReviewsHeader>
          <ReviewsHeaderRow>
            <SummaryLeft>
              <ReviewsTitle>{title}</ReviewsTitle>
              <ReviewsSummary>
                {sorted.length > 0
                  ? `${sorted.length} review${sorted.length === 1 ? '' : 's'} · Average ${avg}/5`
                  : 'No reviews yet'}
              </ReviewsSummary>
            </SummaryLeft>

            {sorted.length > 0 && (
              <SummaryRight>
                <Rating value={avg} precision={0.1} readOnly />
                <Chip size="small" label={`${avg} / 5`} />
              </SummaryRight>
            )}
          </ReviewsHeaderRow>
        </ReviewsHeader>

        <CardContent sx={{ px: 2.5, py: 1 }}>
          {sorted.length === 0 ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                my: 1,
                border: '1px dashed rgba(26, 26, 46, 0.14)',
                background: '#f2f8f5',
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                No reviews yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reviews will appear here once customers leave feedback.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              {sorted.map((r, idx) => {
                const name = fullName(r?.FirstName, r?.LastName);
                const date = formatDate(r?.CreatedDate);
                const rating = Math.max(0, Math.min(5, Number(r?.Rating) || 0));
                const text = (r?.ReviewText || '').trim();

                return (
                  <ReviewRow key={r?.ReviewId || idx}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <AvatarBrand>{getInitials(r?.FirstName, r?.LastName)}</AvatarBrand>

                      <Box sx={{ flex: 1 }}>
                        <NameAndMeta>
                          <Typography sx={{ fontWeight: 800 }}>{name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {date}
                          </Typography>
                        </NameAndMeta>

                        <RatingLine>
                          <Rating value={rating} readOnly size="small" />
                          <Typography variant="body2" color="text.secondary">
                            {rating}/5
                          </Typography>
                        </RatingLine>

                        {text ? (
                          <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                            {text}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, fontStyle: 'italic' }}
                            color="text.secondary"
                          >
                            No written review provided.
                          </Typography>
                        )}
                      </Box>
                    </Stack>

                    {idx !== sorted.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </ReviewRow>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </ReviewsCard>
    </ReviewsSection>
  );
}
